import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/suporte/')({ component: AdminSupportIndex });

type Ticket = {
  id: string;
  status: string;
  hasUnreadByAdmin: boolean;
  updatedAt: string;
  user: { id: string; name: string | null; email: string; role: string; avatar: string | null };
  lastMessage: { content: string; fromAdmin: boolean; createdAt: string } | null;
};

function AdminSupportIndex() {
  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ['admin-support-tickets'],
    queryFn: () => api.get('admin/support/tickets').json<Ticket[]>(),
    refetchInterval: 15000,
  });

  const unread = tickets.filter((t) => t.hasUnreadByAdmin).length;

  return (
    <>
      <AdminPageHeader
        title="Suporte"
        subtitle={`${tickets.length} ticket${tickets.length === 1 ? '' : 's'} · ${unread} não lido${unread === 1 ? '' : 's'}`}
      />

      {isLoading ? (
        <div className="bg-white rounded-3xl p-12 animate-pulse h-32 border border-slate-100" />
      ) : tickets.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
          <p className="text-slate-500">Nenhum ticket de suporte ainda.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {tickets.map((t) => (
            <li key={t.id}>
              <Link
                to="/admin/suporte/$userId"
                params={{ userId: t.user.id }}
                className={cn(
                  'block bg-white rounded-2xl p-4 border shadow-sm hover:bg-slate-50 transition',
                  t.hasUnreadByAdmin ? 'border-teal-200' : 'border-slate-100',
                )}
              >
                <div className="flex items-center gap-4">
                  {t.user.avatar ? (
                    <img src={t.user.avatar} alt="" className="size-12 rounded-full object-cover ring-2 ring-teal-100 shrink-0" />
                  ) : (
                    <div className="size-12 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold shrink-0">
                      {(t.user.name ?? t.user.email)?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-800 truncate">{t.user.name ?? '(sem nome)'}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                        {t.user.role}
                      </span>
                      {t.hasUnreadByAdmin && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-coral-500 text-white font-semibold">
                          NOVO
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 truncate">{t.user.email}</div>
                    {t.lastMessage && (
                      <p className="text-sm text-slate-600 mt-1 truncate">
                        <span className="font-semibold text-slate-400">{t.lastMessage.fromAdmin ? 'Você: ' : ''}</span>
                        {t.lastMessage.content}
                      </p>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 whitespace-nowrap">
                    {new Date(t.updatedAt).toLocaleString('pt-BR', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
